import { useState, useEffect } from 'react'
import { Menu, Bell, Search, LogOut, X, ChevronDown } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { useSearchStore } from '../store/searchStore'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { pacienteService } from '../services/pacienteService'
import { PacienteConSaldo } from '../types'
import SearchDropdown from './SearchDropdown'
import ErrorBoundary from './ErrorBoundary'
import { apiService } from '../services/api'
import { useLanguage } from '../hooks/useLanguage'
import { t } from '../utils/translations'
import { getBogotaDate } from '../utils/dateUtils'

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false)
  const [currentDate, setCurrentDate] = useState('')
  const { logout } = useAuthStore()
  const { globalSearchTerm, setGlobalSearchTerm, clearSearch } = useSearchStore()
  const navigate = useNavigate()
  const language = useLanguage()

  // Query para obtener pacientes con saldo negativo
  const { data: pacientesSaldoNegativo, isLoading: isLoadingNotifications } = useQuery({
    queryKey: ['pacientes-saldo-negativo'],
    queryFn: () => pacienteService.getPacientesConSaldoNegativo(5),
    refetchInterval: 30000, // Refrescar cada 30 segundos
  })

  // Obtener configuración de mostrar temperatura de Botox
  const getShowBotoxTemperature = () => {
    const user = useAuthStore.getState().user
    if (!user?.configuracion) return true // Por defecto mostrar
    
    try {
      const config = typeof user.configuracion === 'string' 
        ? JSON.parse(user.configuracion) 
        : user.configuracion
      
      return config.showBotoxTemperature !== undefined 
        ? config.showBotoxTemperature 
        : (config.show_botox_temperature !== undefined ? config.show_botox_temperature : true)
    } catch {
      return true
    }
  }

  const showBotoxTemperature = getShowBotoxTemperature()

  // Query para obtener temperatura de Botox
  const { data: temperaturaData, isLoading: isLoadingTemperatura } = useQuery({
    queryKey: ['temperatura-botox'],
    queryFn: async () => {
      try {
        return await apiService.get<unknown>('/temperaturas/latest', { timeout: 5000 })
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response?.status
        if (status !== 403) {
          console.error('Error al obtener temperatura de Botox:', error)
        }
        return null
      }
    },
    enabled: showBotoxTemperature, // Solo ejecutar si está habilitado
    refetchInterval: showBotoxTemperature ? 120000 : false, // Refrescar cada 2 minutos solo si está habilitado
    retry: 0, // No reintentar para evitar múltiples llamadas fallidas
    staleTime: 30000, // Considerar los datos válidos por 30 segundos
  })

  // Función para obtener el color según la temperatura
  const getTemperatureColor = (tempC: number) => {
    // Rojo: < 0°C o > 8°C
    if (tempC < 0 || tempC > 8) {
      return 'text-red-600'
    }
    // Naranja: > 0 y < 2 o > 5 y < 8
    if ((tempC > 0 && tempC < 2) || (tempC > 5 && tempC < 8)) {
      return 'text-orange-600'
    }
    // Verde: >= 2°C y <= 5°C
    if (tempC >= 2 && tempC <= 5) {
      return 'text-green-600'
    }
    return 'text-gray-600' // Default (para casos edge como exactamente 0, 2, 5, 8)
  }

  const getLocale = () => {
    switch (language) {
      case 'en':
        return 'en-US'
      case 'ca':
        return 'ca-ES'
      default:
        return 'es-ES'
    }
  }

  // Función para formatear la fecha
  const formatCurrentDate = () => {
    const bogotaDate = getBogotaDate()
    return bogotaDate.toLocaleDateString(getLocale(), { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  }

  // Actualizar la fecha cada minuto
  useEffect(() => {
    const updateDate = () => {
      setCurrentDate(formatCurrentDate())
    }
    
    // Actualizar inmediatamente
    updateDate()
    
    // Actualizar cada minuto
    const interval = setInterval(updateDate, 60000)
    
    return () => clearInterval(interval)
  }, [language])

  // Cerrar notificaciones al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isNotificationsOpen) {
        const target = event.target as Element
        if (!target.closest('[data-notifications]')) {
          setIsNotificationsOpen(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isNotificationsOpen])

  // Cerrar dropdown de búsqueda cuando se pierde el foco
  useEffect(() => {
    if (!globalSearchTerm.trim()) {
      setIsSearchDropdownOpen(false)
    }
  }, [globalSearchTerm])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (globalSearchTerm.trim()) {
      // Navegar a la página de pacientes con el término de búsqueda
      navigate(`/pacientes?search=${encodeURIComponent(globalSearchTerm.trim())}`)
      setIsSearchDropdownOpen(false)
    }
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setGlobalSearchTerm(value)
    
    // Mostrar dropdown si hay texto
    if (value.trim().length > 0) {
      setIsSearchDropdownOpen(true)
    } else {
      setIsSearchDropdownOpen(false)
    }
  }

  const handleSearchFocus = () => {
    if (globalSearchTerm.trim().length > 0) {
      setIsSearchDropdownOpen(true)
    }
  }

  const handleSearchBlur = () => {
    // Delay para permitir clicks en el dropdown
    setTimeout(() => {
      setIsSearchDropdownOpen(false)
    }, 150)
  }

  const handleClearSearch = () => {
    clearSearch()
    setIsSearchDropdownOpen(false)
  }

  const handleSearchResultClick = () => {
    setIsSearchDropdownOpen(false)
  }

  const handleNotificationClick = () => {
    setIsNotificationsOpen(!isNotificationsOpen)
  }

  const handlePacienteClick = (pacienteConSaldo: PacienteConSaldo) => {
    setIsNotificationsOpen(false)
    navigate(`/pacientes?search=${encodeURIComponent(pacienteConSaldo.paciente.nombre_completo)}`)
  }


  return (
    <header className="shrink-0 z-20 bg-white dark:bg-gray-800 shadow-xs border-b border-gray-200 dark:border-gray-700">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center h-16 gap-4">
          {/* Left side - Mobile menu button and Search */}
          <div className="flex items-center gap-4 min-w-0">
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                type="button"
                className="bg-white dark:bg-gray-800 p-2 rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-primary-500"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <span className="sr-only">Abrir menú principal</span>
                <Menu className="h-6 w-6" />
              </button>
            </div>

            {/* Search bar */}
            <div className="flex-1 relative">
              <form onSubmit={handleSearch} className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={globalSearchTerm}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  placeholder={t('buscar-placeholder', language)}
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-hidden focus:placeholder-gray-400 dark:focus:placeholder-gray-500 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                {globalSearchTerm && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </form>
              
              {/* Search dropdown */}
              <ErrorBoundary>
                <SearchDropdown
                  isOpen={isSearchDropdownOpen}
                  searchTerm={globalSearchTerm}
                  onClose={() => setIsSearchDropdownOpen(false)}
                  onResultClick={handleSearchResultClick}
                />
              </ErrorBoundary>
            </div>
          </div>

          {/* Center - Botox Temperature */}
          {showBotoxTemperature && (
            <div className="flex justify-center items-center min-w-0 shrink-0 px-2">
              {temperaturaData?.data?.Fridge_Botox ? (
                <div className="flex items-center space-x-2 text-sm">
                  <span className="text-gray-600 dark:text-gray-400">{t('botox', language)}:</span>
                  <span className={`font-medium ${getTemperatureColor(temperaturaData.data.Fridge_Botox.tempC)}`}>
                    {temperaturaData.data.Fridge_Botox.tempC.toFixed(2)}°C
                  </span>
                </div>
              ) : isLoadingTemperatura ? (
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <span className="animate-pulse">...</span>
                </div>
              ) : null}
            </div>
          )}

          {/* Right side */}
          <div className="flex items-center justify-end space-x-4">
            {/* Current Date */}
            <div className="hidden sm:flex items-center space-x-2 text-sm text-gray-600">
              <span className="font-medium">{currentDate}</span>
            </div>

            {/* Notifications */}
            <div className="relative" data-notifications>
              <button
                type="button"
                onClick={handleNotificationClick}
                className="bg-white dark:bg-gray-800 p-2 rounded-full text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 relative"
              >
                <span className="sr-only">Ver notificaciones</span>
                <Bell className="h-5 w-5" />
                {/* Notification badge */}
                {pacientesSaldoNegativo && pacientesSaldoNegativo.length > 0 && (
                  <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white"></span>
                )}
              </button>

              {/* Notifications dropdown */}
              {isNotificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black dark:ring-gray-700 ring-opacity-5 z-50">
                  <div className="py-1">
                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {t('pacientes-saldo-pendiente', language)}
                      </h3>
                    </div>
                    
                    {isLoadingNotifications ? (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {t('cargando', language)}
                      </div>
                    ) : pacientesSaldoNegativo && pacientesSaldoNegativo.length > 0 ? (
                      pacientesSaldoNegativo.map((pacienteConSaldo) => (
                        <button
                          key={pacienteConSaldo.paciente.id}
                          onClick={() => handlePacienteClick(pacienteConSaldo)}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-hidden focus:bg-gray-50 dark:focus:bg-gray-700"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {pacienteConSaldo.paciente.nombre_completo}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {pacienteConSaldo.paciente.numero_documento}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-red-600 dark:text-red-400">
                                ${Math.abs(pacienteConSaldo.saldo).toFixed(2)}
                              </span>
                              <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                            </div>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {t('sin-notificaciones', language)}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Logout */}
            <button
              onClick={logout}
              type="button"
              title={t('cerrar-sesion', language)}
              className="bg-white p-2 rounded-full text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 relative"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu — scroll si hay muchos ítems */}
      {isMobileMenuOpen && (
        <div className="md:hidden max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <a
              href="/dashboard"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Dashboard
            </a>
            <a
              href="/pacientes"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Pacientes
            </a>
            <a
              href="/atenciones"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Atenciones
            </a>
            <a
              href="/tratamientos"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Tratamientos
            </a>
            <a
              href="/reportes"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Reportes
            </a>
            <a
              href="/configuracion"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Configuración
            </a>
          </div>
        </div>
      )}
    </header>
  )
}
